import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import { ArrowUpRight, Play, Clock } from 'lucide-react';
import { api } from '../lib/api';
import SEOHead from '../components/common/SEOHead';

/**
 * Video Centre — Netflix-meets-YouTube hub for BestNeighbourhoods.ca.
 *
 * A cinematic featured strip on top, a sticky category rail, and horizontal
 * "shelves" per category — each with a horizontally-scrollable set of video
 * thumbnails that expand into a preview on hover.
 */

const fmtDuration = (s) => {
    if (!s) return '';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
};

export default function VideoHub() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeCategory = searchParams.get('category') || null;

    const { data: catsData } = useQuery({
        queryKey: ['video-categories'],
        queryFn: async () => (await api.get('/videos/categories')).data,
    });
    const { data: videosData } = useQuery({
        queryKey: ['videos', activeCategory],
        queryFn: async () => (await api.get('/videos', { params: activeCategory ? { category: activeCategory } : {} })).data,
    });

    const categories = catsData?.items || [];
    const videos = videosData?.items || [];
    const featured = videos[0];

    // Group by category for the shelf layout when no filter
    const byCategory = categories.reduce((acc, c) => {
        acc[c.slug] = videos.filter((v) => v.category === c.slug);
        return acc;
    }, {});

    return (
        <div className="bg-[#0A0A0A] text-white min-h-screen">
            <SEOHead
                title="Video Centre — Southern Ontario Neighbourhood Intelligence"
                description="Neighbourhood tours, market updates, community guides, investment analysis, and more. Every video with a transcript, article, FAQs and related reports."
                path="/videos"
            />

            {/* ==================== FEATURED HERO ==================== */}
            {featured && !activeCategory && (
                <section className="relative w-full overflow-hidden" style={{ minHeight: 'min(78vh, 720px)' }}>
                    <VideoBackdrop v={featured} />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.1) 40%, rgba(10,10,10,0.95) 100%)' }} />
                    <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 min-h-[inherit] flex flex-col justify-end pt-24 pb-16 lg:pb-20" style={{ minHeight: 'min(78vh, 720px)' }}>
                        <div className="text-[11px] tracking-[0.24em] uppercase text-white/70 font-mono-data flex items-center gap-3">
                            <span className="inline-block w-6 h-px bg-white/60" />
                            Video Centre · Featured
                        </div>
                        <h1
                            className="font-editorial mt-6 tracking-editorial"
                            style={{ fontSize: 'clamp(40px, 6.2vw, 88px)', lineHeight: 1.02, letterSpacing: '-0.03em' }}
                            data-testid="video-featured-title"
                        >
                            {featured.title}
                        </h1>
                        <p className="mt-6 font-editorial italic text-white/85 max-w-2xl" style={{ fontSize: '19px', lineHeight: 1.5 }}>
                            {featured.dek}
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link
                                to={`/videos/${featured.slug}`}
                                data-testid="video-featured-watch"
                                className="inline-flex items-center gap-2 bg-white text-black px-7 py-4 text-[14px] font-medium hover:bg-neutral-200"
                                style={{ letterSpacing: '0.02em' }}
                            >
                                <Play className="w-4 h-4" />
                                Watch now
                            </Link>
                            <Link
                                to="/match"
                                className="inline-flex items-center gap-2 border border-white/60 text-white px-7 py-4 text-[14px] font-medium hover:bg-brand-gold hover:text-brand-navy transition-colors"
                               
                            >
                                Take the Match Assessment
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* ==================== CATEGORY RAIL ==================== */}
            <div className="sticky top-16 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center gap-6 overflow-x-auto no-scrollbar">
                    <button
                        onClick={() => setSearchParams({})}
                        className={`whitespace-nowrap text-[13px] tracking-[0.16em] uppercase font-mono-data hover:text-white transition-colors ${!activeCategory ? 'text-white' : 'text-white/50'}`}
                        data-testid="video-cat-all"
                    >
                        All videos
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c.slug}
                            onClick={() => setSearchParams({ category: c.slug })}
                            data-testid={`video-cat-${c.slug}`}
                            className={`whitespace-nowrap text-[13px] tracking-[0.16em] uppercase font-mono-data hover:text-white transition-colors ${activeCategory === c.slug ? 'text-white' : 'text-white/50'}`}
                        >
                            {c.name}
                            <span className="ml-2 text-white/30 tabular text-[11px]">{c.count}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* ==================== SHELVES ==================== */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                {activeCategory ? (
                    <VideoGrid videos={videos} />
                ) : (
                    categories
                        .filter((c) => (byCategory[c.slug] || []).length > 0)
                        .map((c) => (
                            <Shelf key={c.slug} category={c} videos={byCategory[c.slug]} />
                        ))
                )}
            </section>
        </div>
    );
}


function VideoBackdrop({ v }) {
    // Try to play video_url muted-loop as the ambient bg; fall back to thumbnail image
    if (v.video_url) {
        return (
            <video
                src={v.video_url}
                muted loop autoPlay playsInline
                poster={v.thumbnail}
                className="absolute inset-0 w-full h-full object-cover opacity-90"
                aria-hidden="true"
            />
        );
    }
    return (
        <img
            src={v.thumbnail}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
    );
}


function Shelf({ category, videos }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
            className="mb-16"
            data-testid={`video-shelf-${category.slug}`}
        >
            <div className="flex items-end justify-between gap-6 mb-5">
                <div>
                    <div className="font-mono-data text-[10px] tracking-[0.24em] uppercase text-white/50">
                        {category.name}
                    </div>
                    <h2 className="font-editorial mt-2 tracking-editorial text-white leading-tight" style={{ fontSize: '30px', letterSpacing: '-0.01em' }}>
                        {category.blurb}
                    </h2>
                </div>
                <Link
                    to={`/videos?category=${category.slug}`}
                    className="text-[11.5px] tracking-[0.18em] uppercase text-white/60 hover:text-white font-mono-data flex items-center gap-1 whitespace-nowrap"
                >
                    Browse {category.name.toLowerCase()} <ArrowUpRight className="w-3 h-3" />
                </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {videos.map((v) => <VideoCard key={v.slug} v={v} />)}
            </div>
        </motion.div>
    );
}


function VideoGrid({ videos }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {videos.map((v) => <VideoCard key={v.slug} v={v} />)}
        </div>
    );
}


function VideoCard({ v }) {
    const [hover, setHover] = useState(false);
    const vRef = useRef(null);
    useEffect(() => {
        if (hover && vRef.current) {
            vRef.current.play?.().catch(() => {});
        }
    }, [hover]);
    return (
        <Link
            to={`/videos/${v.slug}`}
            data-testid={`video-card-${v.slug}`}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className="group block relative overflow-hidden bg-neutral-900 border border-white/10 hover:border-white/40 transition-colors"
        >
            <div className="relative aspect-video overflow-hidden bg-neutral-800">
                <img src={v.thumbnail} alt={v.title} loading="lazy" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hover && v.video_url ? 'opacity-0' : 'opacity-100'}`} />
                {v.video_url && (
                    <video ref={vRef} src={v.video_url} muted loop playsInline className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hover ? 'opacity-100' : 'opacity-0'}`} />
                )}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.75) 100%)' }} />
                {v.duration_sec && (
                    <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 text-[11px] font-mono-data tabular flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {fmtDuration(v.duration_sec)}
                    </div>
                )}
                <div className="absolute bottom-3 left-3 right-3">
                    <div className="font-editorial text-white leading-tight tracking-editorial line-clamp-2" style={{ fontSize: '18px', letterSpacing: '-0.005em' }}>
                        {v.title}
                    </div>
                </div>
            </div>
            <div className="p-4">
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 font-mono-data">
                    {v.category?.replace(/-/g, ' ')}
                </div>
                <p className="mt-2 text-[13px] text-white/70 leading-relaxed line-clamp-2">{v.dek}</p>
            </div>
        </Link>
    );
}
